import {
  FormContext,
  FormContextProvider,
  SchemaFieldsWithContextUI,
} from "@stormwater-software/lib-form";
import { LayoutTypes, Question } from "@stormwater-software/lib-inspection-templates";
import { TooltipContextProvider } from "@stormwater-software/lib-ui";
import { set,difference } from "lodash";
import React, { MouseEventHandler, useContext } from "react";
import QuestionGroupContext from "../../../../contexts/QuestionGroupContext";
import TemplateEditorContext from "../../../../contexts/TemplateEditorContext";
import { defaultInspectionTypeOptions, useInspectionTypes, useInspectionTypesByTemplate } from "../../../../hooks/inspectionTypes";
import { FormModal, SubmitHandlerFn } from "../../../Shared/form";
import Loading from "../../../Shared/ResourceIndex/Loading";

export interface IQuestionDetailFormProps {
  title?: string;
  question?: Question;
  questionValues?: Array<any>;
  onCancel: MouseEventHandler;
  onSubmit: SubmitHandlerFn;
  order: number;
  isUsedTemplate?: boolean;
}

const QuestionDetailForm: React.FC<IQuestionDetailFormProps> = ({
  question = null,
  questionValues = null,
  onCancel,
  title = "Edit Question",
  onSubmit = () => Promise.resolve(),
  order,
  isUsedTemplate = false,
}) => {
  const templateEditorContext = useContext(TemplateEditorContext);
  const questionGroupContext = useContext(QuestionGroupContext);
  const allInspectionTypesQuery = useInspectionTypes(); // All available inspection types
  const templateInspectionTypesQuery = useInspectionTypesByTemplate(templateEditorContext.templateId); // Template-specific inspection types

  const getInitialValues = () => {
    if (question && questionValues) {
      const vals: { [key: string]: any } = {};

      // Global fields (input_label, description, question_width) keyed by questionFieldId.
      templateEditorContext.questionConfigData.forEach(config => {
        const value = questionValues.find(
          _ => _.questionFieldId === config.id,
        );

        if (value) {
          vals[`${config.fieldName}`] = value.value;
        }
      });

      // Per-type fields (features + options) keyed by questionTypeFieldId.
      templateEditorContext.questionConfigData.forEach((config: any) => {
        const value = questionValues.find(
          _ => _.questionTypeFieldId === config.id,
        );

        if (value) {
          vals[`${config.fieldName}`] =
            config.fieldName === "options"
              ? JSON.parse(value.value)
              : value.value;
        }
      });

      return {
        ...question,
        ...vals,
      };
    } else {
      return {
        groupId: questionGroupContext.questionGroup.id,
        sort_order: order,
        features: [],
      };
    }
  };

  const getSchema = (values: Record<string, any>) => {
    const schema = Object.assign({}, templateEditorContext.defaultSchema);

    templateEditorContext.questionConfigData.forEach((item: any) => {
      if (item.dataSource === "QuestionFeature") {
        schema[item.fieldName].style = { display: "none" };
      }

      return item;
    });

    if (!values.question_type_id || !templateEditorContext.questionsWithOptions.includes(parseInt(values.question_type_id))) {
      delete schema.options;
    }

    if(templateEditorContext.questionConfigData) {
      templateEditorContext.questionConfigData.map((item: any) => {
        if (item.dataSource === "QuestionFeature" && values.question_type_id && item.questionTypeId === parseInt(values.question_type_id)) {
          schema[item.fieldName].style = { display: "block" };
        }
        
        return item;
      });
    }
    
    if (questionGroupContext.questionGroup.layout_type === LayoutTypes["TABLE"]) {
      delete schema.question_width;
    }

    if (questionGroupContext.questionGroup.layout_type === LayoutTypes["FORM"] && values.question_type_id !== 5) {
      delete schema.comment;
    }


    if (
      values.isInspectionType &&
      (values.question_type_id !== 5 ||
        JSON.stringify(values.options) !==
          JSON.stringify(templateInspectionTypesQuery.data!.inspectionTypes))
    ) {
      if (
        !values.options ||
        values.options.length < 1 ||
        difference(values.options, allInspectionTypesQuery.data).length !== 0
      ) {
        if (title === "Edit Question") set(values, 'options', Array.from(new Set([...(values.options || []), ...(templateInspectionTypesQuery.data!.inspectionTypes || []), ...defaultInspectionTypeOptions])));
        else set(values, 'options', allInspectionTypesQuery.data);
      }
      set(values, 'question_type_id', 5); // id of Select Many
    }

    if (values.options) {
      set(schema, 'options', { ...schema.options, disabled: false });
    }

    if (values.isInspectionType) {
      set(schema, 'options', {
        ...schema.options,
        isInspectionType: true,
        disabled: false,
        options: allInspectionTypesQuery.data, // All available options for the "Add More" dropdown
      });
    }

    if (isUsedTemplate) {
      set(schema, "force_update", { ...schema.force_update, style: { display: "block" } });
    }

    if (!values.precipHistory && !values.sumOfPrecip) {
      delete schema.forecast_days;
    }

    set(schema, 'question_type_id', {
      ...schema.question_type_id,
      disabled: false,
    });

    return schema;
  };

  if (allInspectionTypesQuery.isLoading || !allInspectionTypesQuery.data || 
      templateInspectionTypesQuery.isLoading || !templateInspectionTypesQuery.data!.inspectionTypes) {
    return <Loading />;
  }

  return (
    <TooltipContextProvider>
      <FormContextProvider>
        <FormContext.Consumer>
          {formContext => (
            <FormModal
              onCancel={onCancel}
              onSubmit={onSubmit}
              modalProps={{ title: title }}
            >
              <SchemaFieldsWithContextUI
                schema={getSchema(formContext.value)}
                initialFormData={getInitialValues()}
              />
            </FormModal>
          )}
        </FormContext.Consumer>
      </FormContextProvider>
    </TooltipContextProvider>
  );
};

export default QuestionDetailForm;
